import { ReduxAssistantState, uiActions } from 'careand-redux/slices/ui/ui';
import _ from 'lodash';
import { store, useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { AdminImage, PatientImage } from 'components/Common/UserImage';
import React, { MouseEventHandler, ReactElement, useEffect, useMemo, useState } from 'react';
import AdminUtils from 'services/AdminUtils';
import AssistantChat from '../AssistantChat';
import AdminTopBar from '../TopBars/AdminTopBar';
import './style.scss';
import { useAsyncMemo } from 'services/TypeUtils';
import { api } from 'careand-redux/api/ApiThunks';
import MiscUtils from 'services/MiscUtils';
import { IconButton } from '@material-ui/core';
import Icon from 'components/Utils/NewIcon';
import { CustomLoadingDataSpinner } from 'components/Common/LoadingDataSpinner';
import UnreadBadge from 'components/Common/UnreadBadge';
import { AssistantUnreadTotal } from 'careand-redux/selectors';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import RouteUtils from 'services/RouteUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ProgressMessage from '../../Utils/ProgressMessage';
import { SavedAIConversation } from '../../../generated/api';
import PerformanceUtils from '../../../services/PerformanceUtils';
import ChatUtils from '../../../services/ChatUtils';

const AssistantChatManager = () => {
  const dispatch = useAppDispatch();
  const assistants = useAppSelector((state) => state.ui.assistants);
  const openAssistant = useAppSelector((state) => state.ui.openAssistantId);
  const admins = useAppSelector((state) => state.admins.byId);
  const carey = AdminUtils.getAssistantUser(admins);
  const [showConversations, setShowConversations] = useState<boolean>(false);
  const totalUnread = useAppSelector(AssistantUnreadTotal);
  const [fullscreen, setFullscreen] = useState(false);

  const [savedConversations, setSavedConversations] = useState<SavedAIConversation[] | undefined>();

  const onClose = () => {
    dispatch(uiActions.showAssistant(undefined));
  };

  useEffect(() => {
    if (!openAssistant) {
      setShowConversations(false);
      setFullscreen(false);
    }
  }, [openAssistant]);

  useEffect(() => {
    if (!savedConversations) {
      dispatch(api.AI.getAssistantConversations()).then((response) => {
        const assistants = response?.map((it) => ({
          ...it,
          id: it.id!,
          unread: 0,
        }));

        dispatch(uiActions.addSavedAssistants(assistants || []));
        setSavedConversations(response || []);
      });
    }
  }, [savedConversations, dispatch]);

  const onClick = (assistantId: string) => {
    dispatch(uiActions.showAssistant(assistantId));
  };

  const onNewChat = () => {
    dispatch(uiActions.addAssistant({ setActive: true }));
    setShowConversations(false);
  };

  const onArchive = (assistantId: string) => {
    const idx = _.findIndex(assistants, (it) => it.id === assistantId);
    const matchingFavorite = savedConversations?.find((it) => it.id === assistantId);
    const nextAssistant = assistants[idx + 1] ?? assistants[idx - 1];
    dispatch(uiActions.removeAssistant(assistantId));
    if (matchingFavorite) onRemoveFromFavorites(matchingFavorite);

    if (nextAssistant) {
      dispatch(uiActions.showAssistant(nextAssistant.id));
    } else {
      onClose();
    }
  };

  const openAssistantObject = useMemo(() => {
    return assistants.find((it) => it.id === openAssistant);
  }, [assistants, openAssistant]);

  const favoritesMap = useMemo(() => {
    let map: { [key: string]: boolean } = {};
    savedConversations?.forEach((it) => {
      map[it.id!] = true;
    });
    return map;
  }, [savedConversations]);

  useEffect(() => {
    assistants.forEach((it) => {
      let matching = savedConversations?.find((saved) => saved.id === it.id);
      if (matching) {
        if (it.messages?.length > matching.messages?.length) {
          onAddToFavorites(it);
        }
      }
    });
  }, [assistants, savedConversations]);

  const onToggleFavorite = async (conversation: SavedAIConversation) => {
    if (conversation) {
      if (favoritesMap[conversation.id!]) {
        return onRemoveFromFavorites(conversation);
      } else {
        return onAddToFavorites(conversation);
      }
    }
  };

  const onAddToFavorites = async (conversation: SavedAIConversation) => {
    if (conversation) {
      let patient_id: string | undefined = undefined;
      conversation.messages.forEach((message) => {
        if (!patient_id) {
          patient_id = ChatUtils.extractPatientIdFromMessage(message.content);
        }
      });

      let response = await dispatch(
        api.AI.saveAssistantConversation({
          savedAIConversation: { ...conversation, patient_id },
        }),
      );
      if (response) {
        setSavedConversations((prevState) =>
          PerformanceUtils.editOrAdd(response!, prevState || []),
        );
      }
    }
  };

  const onRemoveFromFavorites = async (conversation: SavedAIConversation) => {
    if (conversation) {
      await dispatch(api.AI.removeAssistantConversation({ id: conversation.id! }));
      setSavedConversations((prev) => PerformanceUtils.removeFromArray(conversation, prev || []));
    }
  };

  const currentChatLoading = openAssistantObject?.loading;

  return (
    <div
      className="assistant-chat-manager"
      data-show-conversations={showConversations}
      data-assistant-open={!!openAssistant}
      data-fullscreen={fullscreen}
    >
      <div className="chat-pane">
        {openAssistant && (
          <AdminTopBar
            key={openAssistantObject?.messages?.length}
            name="Carey (AI Assistant)"
            hideDetails={true}
            onClose={onClose}
            avatar={
              <div className="avatar-wrapper">
                {currentChatLoading && <ProgressMessage size={66} />}
                <AdminImage user={carey} size={60} hideTooltip={true} />
              </div>
            }
            archive={() => onArchive(openAssistant)}
            newChat={() => onNewChat()}
            extraLeftButtons={
              <ConversationFavoriteButton
                isFavorite={!!favoritesMap[openAssistant]}
                onClick={(e) => onToggleFavorite(openAssistantObject!)}
                conversation={openAssistantObject!}
              />
            }
            fullScreen={fullscreen}
            setFullScreen={setFullscreen}
            extraButtons={
              <div className="button list-conversations relative">
                <IconButton onClick={() => setShowConversations((prev) => !prev)}>
                  <Icon
                    src="table-list"
                    alt={showConversations ? 'Hide Conversations' : 'Show Conversations'}
                    showTooltip
                    width="20px"
                    height="20px"
                  />
                </IconButton>
                <UnreadBadge
                  key={totalUnread}
                  unreadCount={totalUnread}
                  className="animated heartBeat slow"
                  unreadText={!totalUnread ? '0' : undefined}
                  style={{
                    position: 'absolute',
                    top: '0px',
                    right: '0px',
                    minWidth: 'unset',
                    width: '17px',
                    height: '17px',
                    lineHeight: '16px',
                    translate: '15% -50%',
                    backgroundColor: totalUnread ? '#2693d1' : '#aaafb4',
                  }}
                />
                <CustomLoadingDataSpinner
                  loading={!!assistants.find((it) => it.id !== openAssistant && it.loading)}
                  style={{
                    color: '#2693d1',
                    position: 'absolute',
                    top: '0px',
                    right: '0px',
                    minWidth: 'unset',
                    width: '20px',
                    height: '20px',
                    lineHeight: '18px',
                    translate: '22% -49%',
                  }}
                />
              </div>
            }
          />
        )}
        {assistants.map((assistant) => {
          return (
            <AssistantChat
              key={assistant.id}
              setFullScreen={setFullscreen}
              visible={assistant.id === openAssistant}
              reduxAssistant={assistant}
            />
          );
        })}
      </div>
      <div className="conversations-pane overflow-hidden">
        <div className="h-full px-[.7rem]">
          <div className="conversations-header">
            <div className="title">Conversations</div>
            <IconButton
              onClick={() => setShowConversations(false)}
              style={{ height: '30px', width: '30px', justifySelf: 'end' }}
            >
              <Icon src="xmark" alt="Close panel" showTooltip />
            </IconButton>
          </div>
          <div className="list h-full overflow-auto ">
            {assistants.map((it) => (
              <ConversationCard
                key={it.id}
                assistant={it}
                active={it.id === openAssistant}
                onClick={onClick}
                onArchive={onArchive}
                favoriteButton={
                  <ConversationFavoriteButton
                    isFavorite={!!favoritesMap[it.id]}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onToggleFavorite(it);
                    }}
                    conversation={it}
                    width="15px"
                  />
                }
              />
            ))}
            <DefaultButton
              name="New chat with Carey"
              className="w-full white"
              onClick={() => dispatch(uiActions.addAssistant({ setActive: true }))}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface ConversationFavoriteButtonProps {
  conversation: ReduxAssistantState | SavedAIConversation;
  isFavorite: boolean;
  onClick: MouseEventHandler<HTMLAnchorElement>;
  width?: string;
}
const ConversationFavoriteButton: React.FC<ConversationFavoriteButtonProps> = ({
  conversation,
  isFavorite,
  onClick,
  width = '20px',
}) => {
  return (
    //@ts-expect-error
    <IconButton onClick={onClick}>
      <Icon
        className={isFavorite ? 'blue' : 'dark-grey'}
        src="star"
        fontAwesomeStyle={isFavorite ? 'fas' : undefined}
        alt={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
        showTooltip
        width={width}
        height={width}
      />
    </IconButton>
  );
};

const ConversationCard = ({
  assistant,
  active,
  onClick,
  onArchive,
  favoriteButton,
}: {
  assistant: ReduxAssistantState;
  active: boolean;
  onClick: (assistantId: string) => void;
  onArchive: (assistantId: string) => void;
  favoriteButton?: ReactElement;
}) => {
  const patientId = useMemo(() => {
    let result = assistant.metadata?.patientId || assistant.patient_id;

    if (!result) {
      assistant.messages.forEach((message) => {
        if (!result) {
          result = ChatUtils.extractPatientIdFromMessage(message.content);
        }
      });
    }
    return result;
  }, [assistant]);

  const documentId = assistant.metadata?.documentId;

  const nav = useNavStack(Stacks.Master);
  const dispatch = useAppDispatch();
  const patient = useAppSelector((state) => state.patients.byId[patientId!]);
  const document = useAppSelector(
    (state) => state.healthRecords.byId[patientId!]?.documents.find((it) => it.id === documentId),
  );
  const openCPP = (patientId: string) => {
    const bubble = store.getState().ui.bubbles.find((it) => it.patient_id === patientId);
    if (bubble) {
      dispatch(uiActions.setActiveBubble(bubble));
    } else {
      const route = RouteUtils.getCPPRoute(patient!.id!);
      nav.push(route);
    }
  };

  const thumbnail = useAsyncMemo(async () => {
    if (!document?.thumbnail) {
      return;
    }

    const blob = await dispatch(
      api.Resource.getResource({ resource: { url: document.thumbnail } }),
    );
    if (blob) {
      return MiscUtils.getBlobUrl(blob);
    }
  }, [document]);

  const title = assistant.subject ?? 'Conversation';
  const description = _.last(assistant.messages)?.content ?? '';

  return (
    <div className="conversation-card" data-active={active} onClick={() => onClick(assistant.id)}>
      <UnreadBadge
        key={assistant.unread}
        unreadCount={assistant.unread}
        className="animated heartBeat slow"
      />
      <div className="buttons">
        {patientId && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openCPP(patientId);
            }}
          >
            <Icon src="user" alt="Open Patient CPP" width="15px" showTooltip={true} />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onArchive(assistant.id);
          }}
        >
          <Icon src="box-archive" alt="Archive" width="15px" showTooltip={true} />
        </IconButton>
        {favoriteButton}
      </div>
      <div className="preview">
        {assistant.loading ? (
          <CustomLoadingDataSpinner style={{ color: '#2693d1', marginRight: '10px' }} loading />
        ) : thumbnail ? (
          <img className="thumbnail" src={thumbnail!} />
        ) : (
          patient && <PatientImage size={45} user={patient} />
        )}
      </div>
      <div className="info">
        <div className="title">{title}</div>
        <div className="description">{description?.slice(0, 38) + '...'}</div>
      </div>
    </div>
  );
};

export default AssistantChatManager;
